<template>
  <div>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="title primary--text">{{ $t('orders.title') }}</div>
      <v-spacer/>
      <div class="d-flex justify-space-between flex-wrap">
        <v-btn
          v-if="manualPurchasePermissions['read-purchase-order-template'] && togglePurchase === 'products'"
          class="mr-6 white--text"
          rounded
          color="blue"
          small
          depressed
          @click="useTemplateDialog = true">
          {{ $t('orders.buttons.loadTemplate') }}
        </v-btn>
        <v-btn
          v-if="manualPurchasePermissions['create-purchase-order-template']"
          :disabled="!validOrder"
          class="mr-3 mb-3 mb-md-0 white--text"
          rounded
          color="blue"
          small
          depressed
          @click="createTemplateDialog = true">
          {{ $t('orders.buttons.saveTemplate') }}
        </v-btn>
      </div>
    </div>

    <BalanceDetail :total="total" >
      <v-col class="d-flex justify-space-between">
        <div class="subtitle-2">
          Total de artículos:
        </div>
        <div>{{ totalProducts }}</div>
      </v-col>
      <v-divider vertical />
      <v-col class="d-flex justify-space-between">
        <div class="subtitle-2">
          Compra total:
        </div>
        <div data-cy="totalOrder">${{ total | toFixNumber(2) | numberWithComma }}</div>
      </v-col>
    </BalanceDetail>

    <div
      v-if="!validTaxInformation"
      class="pt-3 px-3">
      <v-alert type="warning">
        <div class="d-flex justify-space-between">
          Necesitas llenar los datos fiscales de tu restaurante para poder realizar una compra.
          <v-btn
            rounded
            outlined
            small
            to="/settings/restaurant">
            Ir a datos fiscales.
          </v-btn>
        </div>
      </v-alert>
    </div>

    <div class="pa-3 d-flex justify-space-between flex-column flex-md-row">
      <div>
        <v-btn-toggle
          class="d-none d-md-inline-flex"
          v-model="togglePurchase"
          mandatory
          color="primary"
          rounded
          dense
        >
          <v-btn
            v-if="manualPurchasePermissions['create-purchase-order-by-products']"
            small
            value="products"
            @click="resetTables"
          >
            Por Productos
          </v-btn>
          <v-btn
            v-if="manualPurchasePermissions['create-purchase-order-by-provider-products']"
            small
            value="providers"
            @click="resetTables"
          >
            Por Proveedores
          </v-btn>
        </v-btn-toggle>
        <v-chip-group
          class="d-flex d-md-none"
          mandatory
          v-model="togglePurchase"
          active-class="primary--text"
        >
          <v-chip
            v-if="manualPurchasePermissions['create-purchase-order-by-products']"
            value="products"
            @click="resetTables">
            Por Productos
          </v-chip>
          <v-chip
            v-if="manualPurchasePermissions['create-purchase-order-by-provider-products']"
            value="providers"
            @click="resetTables">
            Por Proveedores
          </v-chip>
        </v-chip-group>
      </div>

      <div class="d-flex flex-column flex-md-row">
        <v-text-field
          class="mt-3 mt-md-0"
          v-if="togglePurchase === 'providers' && manualPurchasePermissions['create-purchase-order-by-provider-products']"
          v-model="searchProduct"
          append-icon="mdi-magnify"
          label="Busca productos por nombre"
          solo
          rounded
          hide-details
          flat
          dense/>
        <v-btn
          data-cy="submit"
          class="mt-3 mt-md-0 ml-md-3"
          :disabled="!canBuy || !isValid"
          rounded
          color="primary"
          small
          depressed
          @click="openConfirmedOrderDialog">
          {{ $t('orders.buttons.submit') }}
        </v-btn>
      </div>
    </div>

    <v-flex
      v-if="idArea"
      xs12>
      <v-card ref="tables">
        <v-form v-model="isValid" ref="purchaseOrder">
          <v-data-table
            v-if="togglePurchase === 'products' && manualPurchasePermissions['create-purchase-order-by-products']"
            hide-default-footer
            :hide-default-header="$isMobile"
            :headers="table.headers"
            :items="table.items"
            :items-per-page="totalItems"
            class="fixed-header">
            <template v-slot:footer>
              <v-btn
                data-cy="add-product"
                tile
                text
                color="primary"
                width="100%"
                @click="openAddProduct">
                <v-icon>
                  mdi-plus
                </v-icon>
                {{ $t('orders.buttons.addProduct') }}
              </v-btn>
            </template>
            <template v-slot:item.choice.amount="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : 'max-width: 50px'" data-cy="item-amount">
                {{ ((item.choice || {}).amount || 0) | toFixNumber(2) | numberWithComma | prefix('$') }}
              </div>
            </template>
            <template v-slot:item.quantity="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : 'max-width: 50px'">
                <v-text-field
                  data-cy="item-quantity"
                  v-model.number="item.quantity"
                  :rules="[v => !!v || 'La cantidad es requerida', v => v >= 0 || 'La cantidad debe ser mayor o igual a 0']"
                  type="number"
                />
              </div>
            </template>
            <template v-slot:item.total="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : 'max-width: 70px'">
                {{ (item.choice || {}).amount
                  ? item.quantity
                    ? calculatePriceProductWithTaxes(item.choice) * item.quantity
                    : calculatePriceProductWithTaxes(item.choice)
                  : 0  | toFixNumber(2) | numberWithComma | prefix('$')
                }}
                {{ getProductsTotal() }}
              </div>
            </template>
            <template v-slot:item.action="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : ''">
                <v-tooltip left>
                  <template
                    v-slot:activator="{ on }">
                    <v-btn
                      fab
                      x-small
                      color="error"
                      v-on="on"
                      @click="remove(item)">
                      <v-icon>mdi-minus</v-icon>
                    </v-btn>
                  </template>
                  <span>Eliminar producto</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>

          <v-data-table
            v-if="togglePurchase === 'providers'"
            sort-by="name"
            :hide-default-header="$isMobile"
            :headers="providersTable.headers"
            :items="providersTable.items"
            :loading="providersTable.loading"
            :server-items-length="providersTable.total"
            :options="providersTable.options"
            @update:options="updateProvidersTable"
            :footer-props="{
              'items-per-page-options': [25, 50, 100, 150]
            }"
            class="fixed-header">
            <template v-slot:top>
              <div class="px-3 filters">
                <v-row>
                  <v-col cols="12" md="3">
                    <div class="d-flex">
                      <div class="subtitle-2 grey--text mr-3">
                        Filtrar por:
                      </div>
                      <v-select
                        v-model="category"
                        :items="categories"
                        item-text="name"
                        item-value="id"
                        label="Departamento"
                        hide-details
                        dense
                        @change="filterBy"
                      />
                    </div>
                  </v-col>
                </v-row>
              </div>
            </template>
            <template v-slot:footer>
              <v-btn
                tile
                text
                color="primary"
                width="100%"
                @click="openAddProviderDialog">
                <v-icon>
                  mdi-plus
                </v-icon>
                {{ $t('orders.buttons.addProvider') }}
              </v-btn>
            </template>
            <template v-slot:item.description="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : ''">
                {{ item.description }}
              </div>
            </template>
            <template v-slot:item.name="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : ''">
                {{ item.name }} {{ (item.choice && item.choice.brand ? `| ${item.choice.brand.toUpperCase()}` : 'SIN MARCA') }}
              </div>
            </template>
            <template v-slot:item.choice.provider="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : ''">
                {{ item.choice.provider.name }}
              </div>
            </template>
            <template v-slot:item.choice.display="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : ''">
                <div>
                  {{ item.choice.displayQuantity }} {{ item.choice.display.name }}
                </div>
                <div v-if="item.choice.unit.id !== item.choice.display.id" class="grey--text">
                  <template>
                    de {{ item.choice.contentQuantity }} {{ item.choice.unit.name }}
                  </template>
                </div>
              </div>
            </template>
            <template v-slot:item.choice.amount="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : ''">
                <div class="text-end">
                  {{ ((item.choice || {}).amount || 0) | toFixNumber(2) | numberWithComma | prefix('$') }}
                </div>
              </div>
            </template>
            <template v-slot:item.quantity="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : ''">
                <v-text-field
                  v-model.number="item.quantity"
                  @update:error="setError('quantity', item, $event)"
                  type="number"
                />
              </div>
            </template>
            <template v-slot:item.total="{ item }">
              <div :style="$isMobile ? `max-width: ${tableWidth}px` : ''">
                <div class="text-end">
                  {{ item.choice.amount
                    ? item.quantity
                      ? calculatePriceProductWithTaxes(item.choice) * item.quantity
                      : calculatePriceProductWithTaxes(item.choice)
                    : 0  | toFixNumber(2) | numberWithComma | prefix('$')
                  }}
                  {{ getProductsTotal() }}
                </div>
              </div>
            </template>
          </v-data-table>
        </v-form>
      </v-card>
    </v-flex>

    <v-flex
      v-else
      style="margin-top: 40px"
      class="justify-center d-flex">
      <div class="title">
        Selecciona un área para crear la orden de compra
      </div>
    </v-flex>

    <v-dialog
      @keydown.esc="alert=false"
      v-model="alert"
      persistent
      width="500">
      <v-card>
        <v-card-title>
          ¡Ocurrió un error!
        </v-card-title>
        <v-card-text>
          {{ alertMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer/><v-spacer/>
          <v-btn color="primary" @click="alert = false">
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="createTemplateDialog=false"
      v-model="createTemplateDialog"
      max-width="450">
      <v-card>
        <v-card-title>
          Crear plantilla de orden de compra
        </v-card-title>
        <v-card-text>
          <v-text-field
            dense
            label="Nombre de la plantilla"
            outlined
            hide-details
            rounded
            v-model="template.name"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="primary"
            class="mr-4"
            outlined
            rounded
            small
            @click="closeCreateTemplateDialog">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            @click="createTemplate">
            Crear
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="useTemplateDialog=false"
      v-model="useTemplateDialog"
      max-width="600">
      <v-card>
        <v-card-title>
          Usar plantilla de orden de compra
        </v-card-title>
        <v-data-table
          :items="templates.data"
          :headers="templates.headers"
          :server-items-length="templates.total"
          show-select
          single-select
          v-model="templates.selected"
          :options="templates.options"
          @update:options="updateTemplatesTable"
          :footer-props="{
            'items-per-page-options': [10, 15, 20]
          }"
        >
        <template v-slot:item.data-table-select="{ isSelected, select }">
          <v-simple-checkbox
            :ripple="false"
            :value="isSelected"
            @input="select($event)"
          ></v-simple-checkbox>
        </template>
        <template v-slot:item.action="{ item }">
          <div class="d-flex justify-end align-center">
            <v-tooltip
              v-if="$is() === 'manager' || $is() === 'buyer'"
              left>
              <template
                v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  x-small
                  depressed
                  color="primary"
                  class="mr-2"
                  @click="openDuplicateDialog(item)"
                >
                  <v-icon
                    icon
                    small
                  >mdi-content-copy</v-icon>
                </v-btn>
              </template>
              <span>Duplicar plantilla de orden de compra</span>
            </v-tooltip>
            <v-tooltip
              v-if="$is() === 'manager' || $is() === 'buyer'"
              left>
              <template
                v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  x-small
                  depressed
                  color="primary"
                  class="mr-2"
                  @click="openEditDialog(item)"
                >
                  <v-icon
                    icon
                    small
                  >mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Editar plantilla</span>
            </v-tooltip>
            <v-tooltip
              v-if="$is() === 'manager' || $is() === 'buyer'"
              left>
              <template
                v-slot:activator="{ on }">
                <v-btn
                  fab
                  x-small
                  depressed
                  color="error"
                  class="mr-2"
                  v-on="on"
                  @click="openRemoveTemplateDialog(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Eliminar plantilla</span>
            </v-tooltip>
          </div>
        </template>
        </v-data-table>
        <v-card-actions>
          <v-spacer/>
          <v-btn
            color="primary"
            class="mr-4"
            outlined
            rounded
            small
            @click="closeUseTemplateDialog">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            rounded
            small
            depressed
            :disabled="templates.selected.length === 0"
            @click="templateSelected">
            Usar plantilla
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="addProviderDialog=false"
      v-model="addProviderDialog"
      persistent
      width="500">
      <v-card>
        <v-card-title>
          Buscar proveedor
        </v-card-title>
        <v-form ref="addProvider">
          <v-container grid-list-md>
            <v-layout wrap>
              <v-flex md12>
                <v-autocomplete
                  v-model="searchProvider.selected"
                  :items="searchProvider.items"
                  :loading="searchProvider.isLoading"
                  :search-input.sync="searchProvider.search"
                  outlined
                  dense
                  rounded
                  color="primary"
                  hide-details
                  hide-no-data
                  hide-selected
                  item-text="name"
                  item-value="id"
                  label="Buscar proveedor"
                  placeholder="Nombre del proveedor"
                />
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="mr-4"
            rounded
            outlined
            small
            depressed
            @click="closeAddProviderDialog">
            {{ $t('ctas.close') }}
          </v-btn>
          <v-btn
            color="primary"
            rounded
            dark
            small
            depressed
            @click="addProductsProvider">
            {{ $t('ctas.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="confirmedOrderDialog=false"
      v-model="confirmedOrderDialog"
      persistent
      width="500">
      <v-card>
        <v-card-title class="headline">
          Resumen de orden compra
        </v-card-title>
        <v-card-text>
          <div class="text-h6">Usuario: <span class="font-weight-bold">{{ this.user.email }}</span></div>
          <div class="text-h6">Total: <span class="font-weight-bold">${{ total | toFixNumber(2) | numberWithComma }}</span></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            class="mr-4"
            rounded
            outlined
            small
            depressed
            @click="closeConfirmedOrderDialog">
            {{ $t('ctas.close') }}
          </v-btn>
          <v-btn
            color="primary"
            rounded
            dark
            small
            depressed
            @click="create">
            {{ $t('ctas.confirmed') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AddProduct ref="addProductRef" @product-added="handleProductSelected"/>

    <EditTemplate
      @update="updateTable"
      :template="this.template"
      ref="editDialog"
    />

    <DuplicateTemplate
      @update="updateTable"
      :template="this.template"
      ref="duplicateDialog"
    />

    <DeleteTemplate
      @update="updateTable"
      :template="this.template"
      ref="deleteDialog"
    />

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

  </div>
</template>

<script>
import Order from '@/models/Order'
import Restaurant from '@/models/Restaurant'
import DuplicateTemplate from '@/components/modals/DuplicateTemplate'
import EditTemplate from '@/components/modals/EditTemplate'
import DeleteTemplate from '@/components/modals/DeleteTemplate'
import BalanceDetail from '@/components/elements/BalanceDetail'
import AddProduct from '@/components/modals/AddProduct.vue'
import { numeric } from 'vue-mc/validation'
import { cloneDeep, sortBy, debounce } from 'lodash'

export default {
  name: 'CreatePurchase',
  components: {
    BalanceDetail,
    DuplicateTemplate,
    EditTemplate,
    DeleteTemplate,
    AddProduct
  },
  data () {
    let orderModel = new Order()
    let restaurantModel = new Restaurant()

    return {
      orderModel,
      restaurantModel,
      detail: {},
      addProviderDialog: false,
      confirmedOrderDialog: false,
      togglePurchase: 'products',
      isValid: false,
      overlay: false,
      alert: false,
      createTemplateDialog: false,
      useTemplateDialog: false,
      alertMessage: '',
      removeTemplateDialog: false,
      modalVisible: false,
      selectedProduct: '',
      quantity: 1,
      table: {
        headers: [
          { text: 'Producto', value: 'product', sortable: false },
          { text: 'Proveedor', value: 'choice.provider.name', sortable: false },
          { text: 'Precio Unitario', value: 'choice.amount', sortable: false },
          { text: 'Cantidad', value: 'quantity', sortable: false },
          { text: 'Total con impuestos', value: 'total', sortable: false },
          { text: 'Acciones', value: 'action', sortable: false }
        ],
        items: []
      },
      providersTable: {
        headers: [
          { text: 'Producto', value: 'name', sortable: false },
          { text: 'Descripción', value: 'description', sortable: false },
          { text: 'Proveedor', value: 'choice.provider', sortable: false },
          { text: 'Departamento', value: 'category.name', sortable: false },
          { text: 'Presentación', value: 'choice.display', sortable: false },
          { text: 'Precio Unitario', value: 'choice.amount', sortable: false },
          { text: 'Cantidad', value: 'quantity', sortable: false },
          { text: 'Total con impuestos', value: 'total', sortable: false }
        ],
        items: [],
        total: 0,
        loading: false,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 50,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: ['name'],
          sortDesc: []
        },
        filteredProducts: [],
        totalItems: 0
      },
      template: {
        name: null
      },
      templates: {
        data: [],
        total: 0,
        headers: [
          { text: 'Nombre', value: 'name' },
          { text: 'Acciones', value: 'action' }
        ],
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 10,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: ['name'],
          sortDesc: []
        },
        selected: [],
        loading: false
      },
      balance: null,
      total: 0,
      searchProvider: {
        search: '',
        selected: null,
        items: [],
        isLoading: false
      },
      category: '',
      categories: [],
      products: [],
      errors: [],
      searchProduct: '',
      formProduct: {
        rules: {
          product: [v => !!v || this.$t('validations.ingredient')],
          choices: [v => !!v || this.$t('validations.provider')],
          area: [v => !!v || this.$t('validations.area')],
          quantity: [
            v => !!v || this.$t('validations.required'),
            v => v > 0 || 'La cantidad debe ser mayor a 0'
          ]
        }
      }
    }
  },
  watch: {
    'searchProvider.search': debounce(async function (val) {
      this.searchProvider.isLoading = true
      const params = {
        name: `/${val}/i`,
        fetchAll: true,
        isBanned: true,
        allowedProviders: true
      }
      try {
        const res = await this.$api.get(`/v1/providers`, params)
        this.searchProvider.items = res.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.searchProvider.isLoading = false
    }, 600),
    searchProduct: function (val) {
      if (val) {
        const name = new RegExp(val, 'i')
        if (this.category) {
          this.filterBy(this.category)
        } else {
          this.providersTable.items = this.products.filter(item => name.test(item.name)).slice(0, 50)
        }
      } else {
        if (this.category) {
          this.filterBy(this.category)
        } else {
          this.providersTable.items = this.products.slice(0, 50)
        }
      }
    },
    // ToDo mejorar la carga de templates
    async useTemplateDialog (val) {
      if (val) {
        this.overlay = true
        try {
          await this.getTemplates()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    async togglePurchase (val) {
      if (val === 'providers') {
        this.overlay = true
        try {
          await this.getCategories()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    }
  },
  computed: {
    user () {
      return this.$store.state.login.user
    },
    tableWidth: function () {
      return this.$refs.tables.$el.offsetWidth - 32
    },
    idArea: function () {
      return this.$store.state.area.id || null
    },
    restaurant () {
      return this.$store.state.currentRestaurant.data
    },
    validTaxInformation: function () {
      if (this.$store.state.currentRestaurant.taxInformation.data) {
        return (this.$store.state.currentRestaurant.taxInformation.data.taxId && this.$store.state.currentRestaurant.taxInformation.data.taxName)
      }
      return false
    },
    validOrder () {
      return this.togglePurchase === 'products'
        ? this.table.items.length && this.isValid
        : this.providersTable.items.length && this.isValid
    },
    canBuy () {
      return this.validOrder && this.validTaxInformation
    },
    totalItems () {
      return (this.table.items.length + 1)
    },
    totalProducts () {
      return this.table.items.length
    },
    manualPurchasePermissions () {
      return this.$store.state.login.permissions['purchase-orders'].components['manual-purchase-orders-form'].actions
    },
    units () {
      return this.$store.state.login.units.data
    }
  },
  async mounted () {
    if (this.manualPurchasePermissions['create-purchase-order-by-products'] && this.manualPurchasePermissions['create-purchase-order-by-provider-products']) {
      this.togglePurchase = 'products'
    } else {
      if (this.manualPurchasePermissions['create-purchase-order-by-products']) {
        this.togglePurchase = 'products'
      }
      if (this.manualPurchasePermissions['create-purchase-order-by-provider-products']) {
        this.togglePurchase = 'providers'
      }
    }
  },
  methods: {
    updateTable: async function () {
      this.getTemplates()
    },
    closeCreateTemplateDialog () {
      this.createTemplateDialog = false
      this.template = {
        name: null
      }
    },
    async createTemplate () {
      this.overlay = true
      try {
        const payload = {
          name: this.template.name,
          choices: this.togglePurchase === 'products'
            ? this.table.items.map(item => ({ choice: item.choice.uuid, quantity: item.quantity }))
            : this.providersTable.items.map(item => ({ choice: item.choice.uuid, quantity: item.quantity }))
        }
        await this.$api.post('/v1/purchase-templates', payload)
        await this.getTemplates()
        this.closeCreateTemplateDialog()
        this.$toasted.show('La plantilla se creó correctamente', { type: 'success', duration: 5000 })
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    updateTemplatesTable: async function (data) {
      const { sortBy, sortDesc } = data
      await this.getTemplates({
        limit: data.itemsPerPage,
        skip: (data.itemsPerPage * (data.page - 1)),
        sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`
      })
    },
    async getTemplates (params = null) {
      try {
        params = params || {
          limit: this.templates.options.itemsPerPage,
          skip: (this.templates.options.itemsPerPage * (this.templates.page - 1)),
          select: 'name'
        }
        const response = await this.$api.get('/v1/purchase-templates', params)
        this.templates.data = response.data
        this.templates.total = response.total
        this.templates.loading = false
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    async templateSelected () {
      const allowedProviders = this.restaurant.allowedProviders
      const useWhiteList = this.restaurant.useWhiteList

      const templateProducts = await this.$api.get(`/v1/purchase-templates/${this.templates.selected[0]._id}`)
      const allowedProvider = templateProducts.some(x => allowedProviders.some(v => v.provider === x.choice.provider._id))

      if (useWhiteList && !allowedProvider) {
        this.$toasted.show('La plantilla seleccionada no puede ser utilizada debido a que contienen productos de un proveedor que no esta permitido para la compra.', { duration: 3000 })
      } else {
        this.overlay = true
        if (this.togglePurchase === 'products') {
          this.table.items = templateProducts.filter(item => item).map(x => {
            const name = x.choice.provider.name
            return {
              product: x.product.name,
              choice: {
                uuid: x.choice.uuid,
                provider: { name },
                amount: x.choice.amount,
                taxes: x.choice.taxes
              },
              quantity: x.quantity
            }
          })
        }
        if (this.togglePurchase === 'providers') {
          this.providersTable.items = templateProducts.filter(item => item).map(item => {
            const choice = item.choice
            const product = item.product
            const displayUnit = this.units.find(unit => unit._id === choice.displayUnit)
            const contentUnit = this.units.find(unit => unit._id === choice.contentUnit)
            choice.display = displayUnit
            choice.display.id = displayUnit._id
            choice.unit = contentUnit
            choice.unit.id = contentUnit._id
            return {
              name: product.name,
              choice: choice,
              category: product.category,
              quantity: item.quantity,
              rules: {
                numeric
              }
            }
          })
          this.providersTable.total = this.templates.selected[0].choices.length
        }
        this.overlay = false
        this.closeUseTemplateDialog()
      }
    },
    closeUseTemplateDialog () {
      this.useTemplateDialog = false
      this.templates.selected = []
    },
    getProductsTotal () {
      let total
      if (this.togglePurchase === 'products') {
        total = this.table.items
          .filter(product => product.choice)
          .map(product => product.choice.amount
            ? product.quantity > 0
              ? this.calculatePriceProductWithTaxes(product.choice) * product.quantity
              : 0
            : 0
          )
      }
      if (this.togglePurchase === 'providers') {
        total = this.providersTable.items.map(product => product.choice.amount
          ? product.quantity > 0
            ? this.calculatePriceProductWithTaxes(product.choice) * product.quantity
            : 0
          : 0
        )
      }
      const subtotal = total.reduce((acc, num) => { return acc + num }, 0)
      this.total = subtotal
    },
    calculatePriceProductWithTaxes (product) {
      let priceWithTaxes = product.amount
      const hasBaseAmount = (product.taxes || []).every(tax => tax.baseAmount && tax.baseAmount >= 0)

      if (hasBaseAmount) {
        const taxesAmount = product.taxes.reduce((acc, tax) => {
          const base = tax.isQuota ? tax.base : (tax.base / 100)
          return acc + (tax.baseAmount * base)
        }, 0)
        return priceWithTaxes + taxesAmount
      } else {
        let totalTaxes = 0
        const taxes = product.taxes || []
        const ieps = taxes.find(tax => tax.satCode === '003' && tax.base > 0)
        const iva = product.taxes.find(tax => tax.satCode === '002' && tax.base > 0)

        const otherTaxes = taxes
          .filter(tax => !['003', '002'].includes(tax.satCode))

        if (ieps) {
          const iepsAmount = (ieps.isQuota ? ieps.base : ieps.base / 100) * (product.amount - (product.discount || 0))
          priceWithTaxes = priceWithTaxes + iepsAmount
        }

        totalTaxes = otherTaxes.reduce((acc, num) => {
          let taxApplied
          if (num.isQuota) {
            taxApplied = num.base
          } else {
            taxApplied = (num.base / 100) * product.amount
          }
          acc += taxApplied
          return acc
        }, 0)

        if (iva) {
          priceWithTaxes = priceWithTaxes + (priceWithTaxes * (iva.base / 100))
        }

        priceWithTaxes = totalTaxes + priceWithTaxes

        return priceWithTaxes
      }
    },
    activateChoiceMenu (item) {
      this.$refs[`choice_${item.id}`].activateMenu()
    },
    add () {
      this.table.items.push({
        id: this.table.items.length,
        product: '',
        choice: '',
        quantity: 1,
        area: '',
        products: {
          loading: false,
          data: []
        },
        choices: {
          disabled: true,
          data: []
        },
        rules: {
          product: [v => !!v || this.$t('validations.ingredient')],
          choices: [v => !!v || this.$t('validations.provider')],
          area: [v => !!v || this.$t('validations.area')],
          quantity: [
            v => !!v || this.$t('validations.area'),
            v => v > 0 || 'La cantidad debe ser mayor a 0'
          ]
        }
      })
    },
    remove (item) {
      const index = this.table.items.findIndex(i => i.id === item.id)
      this.table.items.splice(index, 1)
      this.getProductsTotal()
    },
    openAddProduct () {
      this.$refs.addProductRef.openModal()
    },
    async productSelected (uuid, choice, quantity) {
      const response = await this.$api.get(`/v1/products/${uuid}`)

      const selectedChoice = response.choices.find(x => x._id === choice)

      this.table.items.push({
        id: this.table.items.length,
        product: response.name,
        choice: selectedChoice,
        'choice.amount': selectedChoice.amount,
        quantity: quantity,
        total: this.calculatePriceProductWithTaxes(selectedChoice) * quantity
      })
    },
    search: debounce(async function (event, item) {
      item.products.loading = true
      const params = { name: `/${event.target.value}/i`, skip: 0, global: true, limit: 20 }
      const products = await this.$api.get('/v1/products', params)
      item.products = {
        data: products.data,
        loading: false
      }
    }, 600),
    openConfirmedOrderDialog () {
      this.confirmedOrderDialog = true
    },
    closeConfirmedOrderDialog () {
      this.confirmedOrderDialog = false
    },
    async create () {
      const payload = {
        paymentMethod: 'balance',
        area: this.idArea,
        choices: this.togglePurchase === 'products'
          ? this.table.items
            .filter(item => item.quantity > 0)
            .map(item => ({ id: item.choice.uuid, quantity: item.quantity }))
          : this.products
            .filter(item => item.quantity > 0)
            .map(item => ({ id: item.choice.uuid, quantity: item.quantity }))
      }
      payload.choices.forEach(item => {
        if (item.quantity === '') {
          this.setError('quantity', item, true)
        } else {
          item.quantity = parseFloat(item.quantity)
        }
      })
      if (!this.isValid || this.errors.length) {
        this.confirmedOrderDialog = false
        const errors = this.errors.map(error => `</br>${error.name}`)
        const message = `Existen datos vacios en los productos: ${errors}`
        this.$toasted.show(
          message,
          {
            action: {
              text: 'Cerrar',
              onClick: (e, toastObject) => {
                toastObject.goAway(0)
              }
            }
          }
        )
      } else {
        this.overlay = true
        try {
          await this.$api.post('/v1/orders', payload)
          this.$toasted.show('La orden se creó correctamente', { duration: 3000 })
          this.resetTables()
          this.confirmedOrderDialog = false
        } catch (error) {
          this.alertMessage = error.message
          this.alert = true
        }
        this.overlay = false
      }
      this.total = 0
    },
    openAddProviderDialog () {
      this.addProviderDialog = true
    },
    closeAddProviderDialog () {
      this.searchProvider = {
        search: '',
        selected: null,
        items: [],
        isLoading: false
      }
      this.addProviderDialog = false
    },
    addProductsProvider: async function () {
      this.overlay = true
      try {
        const params = {
          provider: this.searchProvider.selected,
          sort: 'name',
          global: true
        }
        this.category = ''
        const res = await this.$api.get('/v1/products', params)
        const items = []
        res.data.map(product => {
          if (product.choices.length === 1) {
            const choice = product.choices.find(choice => choice.provider._id === this.searchProvider.selected)
            items.push({
              category: product.category,
              choice: choice,
              choise: product.choices,
              createdAt: product.createdAt,
              name: product.name,
              quantity: 0,
              rules: { numeric },
              type: product.type,
              uuid: product.uuid,
              description: choice.description
            })
          } else {
            for (const choice of product.choices) {
              if (choice.provider._id === this.searchProvider.selected) {
                items.push({
                  category: product.category,
                  choice: choice,
                  choise: product.choices,
                  createdAt: product.createdAt,
                  name: product.name,
                  quantity: 0,
                  rules: { numeric },
                  type: product.type,
                  uuid: product.uuid,
                  description: choice.description
                })
              }
            }
          }
          return product
        })
        this.products = items
        this.providersTable.items = items
        this.providersTable.total = items.length
        this.providersTable.totalItems = items.length
        this.updateProvidersTable(this.providersTable.options)
        this.closeAddProviderDialog()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    resetTables () {
      this.errors = []
      this.table.items = []
      this.providersTable.items = []
      this.providersTable.total = 0
      this.category = ''
    },
    async getCategories () {
      const { data } = await this.$api.get(`/v1/categories`, { sort: 'name' })
      data.unshift({ id: null, name: 'Todas' })
      this.categories = data
    },
    filterBy (categoryId) {
      const data = this.providersTable.options
      const start = data.itemsPerPage * (data.page - 1)
      const end = data.itemsPerPage * data.page
      if (this.category) {
        if (this.searchProduct) {
          const name = new RegExp(this.searchProduct, 'i')
          const filteredProducts = this.products.filter(item => item.category && item.category._id === categoryId && name.test(item.name))
          this.providersTable.items = sortBy(filteredProducts, 'name').slice(start, end)
          this.providersTable.total = filteredProducts.length
          this.filteredProducts = [...filteredProducts]
        } else {
          const filteredProducts = this.products.filter(item => item.category && item.category._id === categoryId)
          this.providersTable.total = filteredProducts.length
          this.providersTable.items = sortBy(filteredProducts, 'name').slice(start, end)
          this.filteredProducts = [...filteredProducts]
        }
      } else {
        this.providersTable.items = sortBy(this.products, 'name').slice(start, end)
        this.providersTable.total = this.providersTable.totalItems
      }
    },
    updateProvidersTable (data) {
      this.providersTable.options = data
      const start = data.itemsPerPage * (data.page - 1)
      const end = data.itemsPerPage * data.page
      const sortParam = 'name'
      const filteredData = (this.category || this.searchProduct) ? this.filteredProducts : this.products
      this.providersTable.items = sortBy(filteredData, sortParam).slice(start, end)
    },
    setError (type, item, error) {
      if (error) {
        const index = this.errors.findIndex(i => i.uuid === item.uuid)
        if (index < 0) {
          this.errors.push(item)
        }
      } else {
        const index = this.errors.findIndex(i => i.uuid === item.uuid)
        if (index >= 0) {
          this.errors.splice(index, 1)
        }
      }
    },
    openEditDialog (item) {
      const template = cloneDeep(item)
      this.template = template
      this.$refs.editDialog.open()
    },
    openDuplicateDialog (item) {
      const template = cloneDeep(item)
      this.template = template
      this.$refs.duplicateDialog.open()
    },
    openRemoveTemplateDialog: async function (item) {
      const template = cloneDeep(item)
      this.removeTemplateDialog = true
      this.template = template
      this.$refs.deleteDialog.open()
    },
    async handleProductSelected (data) {
      const { quantity, uuid, choice } = data
      await this.productSelected(uuid, choice, quantity)
    }
  }
}
</script>
